import React, { useState } from 'react';

import {
    Box,
    Breadcrumbs,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Link,
    Skeleton,
    Switch,
    Table,
    TextField,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { StyledLoadingButton } from '../../../../components/forms/LoadingButton';
import AdminAuthContainer from '../../AdminAuthContainer';
import AdminSsoContainer, { type OidcIdpCreateDataClient, SsoView } from '../../AdminSsoContainer';

const StyledTable = styled(Table)`
    margin-top: 12px;
`;

const StyledSkeleton = styled(Skeleton)`
    height: 70px;
`;

const StyledHeaderContainer = styled.div`
    display: flex;
    margin-bottom: 10px;
`;

const StyledTitle = styled(Typography)`
    font-size: 24px;
    margin-right: auto;
`;

const StyledButton = styled(Button)`
    margin-left: 15px;
`;
const StyledFormDiv = styled(Box)`
    display: flex;
    flex-direction: column;
    margin-top: 12px;
`;

const StyledSaveButtonDiv = styled(Box)`
    display: flex;
    margin-top: 24px;
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
    margin-top: 14px;
`;

const StyledLabelFieldDiv = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledTextField = styled(TextField)`
    width: 400px;
`;

const SelectDiv = styled.div`
    margin-left: 24px;
    display: flex;
    flex-direction: column;
`;

const SsoUpsert: React.FC = () => {
    const { isAdmin, isLoading } = useContainer(AdminAuthContainer);
    const { t } = useTranslation('admin_settings');
    const history = useHistory();
    const { path } = useRouteMatch();
    const params = useParams();
    const {
        idps,
        ssoFetchStatus,
        view,
        setView,
        selectedIdp,
        setIdpId,
        setSelectedIdp,
        idpId,
        createIdp,
        updateIdp,
        deleteIdp,
        fetchIntegrationList
    } = useContainer(AdminSsoContainer);

    const [formState, setFormState] = useState<OidcIdpCreateDataClient>(
        selectedIdp ?? {
            display_name: '',
            client_id: '',
            display_position: undefined,
            is_active: false,
            domain: '',
            settings: {
                auto_provision_users: false,
                auto_update_users: false,
                role_ids_admin: [],
                role_ids_instructor: [],
                role_ids_inst_designer: [],
                oidc_scope: ''
            }
        }
    );
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);

    const handleChange = (event) => {
        const { name, value, checked } = event.target;
        const newValue = event.target.type === 'checkbox' ? checked : value;
        if (name.includes('__')) {
            const [parent, child] = name.split('__');
            setFormState((prevState) => ({
                ...prevState,
                [parent]: {
                    ...prevState[parent],
                    [child]: newValue
                }
            }));
            return;
        }
        setFormState((prevState) => ({
            ...prevState,
            [name]: newValue
        }));
    };

    const goToSsoList = () => {
        setView(SsoView.List);
        setIdpId(null);
        setSelectedIdp(null);
        history.push(path.replace(/\/(create|edit(?:\/\d+)?)(?:\/.*)?$/, ''));
    };

    const handleSave = async (event) => {
        setSaving(true);
        if (view === SsoView.Create) {
            await createIdp(formState);
        } else if (view === SsoView.Edit) {
            await updateIdp(formState);
        }
        await fetchIntegrationList();
        setSaving(false);
        goToSsoList();
    };

    const handleDelete = async (event) => {
        event.preventDefault();
        setDeleting(true);
        await deleteIdp(formState);
        await fetchIntegrationList();
        setDeleting(false);
        goToSsoList();
    };

    const handleDialogClose = () => {
        setDeleteDialogOpen(false);
    };

    if (view === SsoView.List) {
        goToSsoList();
    }
    function checkForEmptyValues(): boolean | undefined {
        const hasAnyIdentifierClaim = formState.settings.claim_email || formState.settings.claim_ext_id;
        const hasAnyNameClaim =
            formState.settings.claim_name ||
            formState.settings.claim_given_name ||
            formState.settings.claim_family_name;

        return (
            !formState.display_name ||
            !formState.domain ||
            !formState.client_id ||
            !formState.settings.oidc_scope ||
            !hasAnyNameClaim ||
            !hasAnyIdentifierClaim
        );
    }

    return (
        <>
            <StyledHeaderContainer>
                <StyledTitle variant="h1">{t('sso.sso')}</StyledTitle>
            </StyledHeaderContainer>
            <StyledBreadcrumbs>
                <Link variant="body1" underline="hover" color="primary" onClick={goToSsoList}>
                    {t('sso.sso')}
                </Link>
                <Typography variant="body1" color="text.primary">
                    {view === SsoView.Create ? t('sso.add-new-sso') : formState.display_name}
                </Typography>
            </StyledBreadcrumbs>
            <StyledFormDiv>
                <StyledLabelFieldDiv>
                    <Typography>{t('sso.display-name')}</Typography>
                    <StyledTextField
                        name="display_name"
                        value={formState.display_name}
                        onChange={handleChange}
                        helperText={t('sso.display-name-helper')}
                        size="small"
                    />
                </StyledLabelFieldDiv>

                <StyledLabelFieldDiv>
                    <Typography>{t('sso.is-active')}</Typography>
                    <Switch name="is_active" checked={formState.is_active} onChange={handleChange} />
                </StyledLabelFieldDiv>

                <StyledLabelFieldDiv>
                    <Typography>{t('sso.domain')}</Typography>
                    <StyledTextField
                        name="domain"
                        value={formState.domain}
                        onChange={handleChange}
                        helperText={t('sso.domain-helper')}
                        size="small"
                    />
                </StyledLabelFieldDiv>

                <StyledLabelFieldDiv>
                    <Typography>{t('sso.client-id')}</Typography>
                    <StyledTextField
                        name="client_id"
                        value={formState.client_id}
                        onChange={handleChange}
                        helperText={t('sso.client-id-helper')}
                        size="small"
                    />
                </StyledLabelFieldDiv>

                <StyledLabelFieldDiv>
                    <Typography>{t('sso.settings-oidc-scope')}</Typography>
                    <StyledTextField
                        name="settings__oidc_scope"
                        value={formState.settings.oidc_scope}
                        onChange={handleChange}
                        helperText={t('sso.settings-oidc-scope-helper')}
                        size="small"
                    />
                </StyledLabelFieldDiv>

                <StyledLabelFieldDiv>
                    <Typography>{t('sso.settings-claim-name')}</Typography>
                    <StyledTextField
                        name="settings__claim_name"
                        value={formState.settings.claim_name}
                        onChange={handleChange}
                        helperText={t('sso.settings-claim-name-helper')}
                        size="small"
                    />
                </StyledLabelFieldDiv>

                <StyledLabelFieldDiv>
                    <Typography>{t('sso.settings-claim-given-name')}</Typography>
                    <StyledTextField
                        name="settings__claim_given_name"
                        value={formState.settings.claim_given_name}
                        onChange={handleChange}
                        helperText={t('sso.settings-claim-given-name-helper')}
                        size="small"
                    />
                </StyledLabelFieldDiv>

                <StyledLabelFieldDiv>
                    <Typography>{t('sso.settings-claim-family-name')}</Typography>
                    <StyledTextField
                        name="settings__claim_family_name"
                        value={formState.settings.claim_family_name}
                        onChange={handleChange}
                        helperText={t('sso.settings-claim-family-name-helper')}
                        size="small"
                    />
                </StyledLabelFieldDiv>

                <StyledLabelFieldDiv>
                    <Typography>{t('sso.settings-claim-email')}</Typography>
                    <StyledTextField
                        name="settings__claim_email"
                        value={formState.settings.claim_email}
                        onChange={handleChange}
                        helperText={t('sso.settings-claim-email-helper')}
                        size="small"
                    />
                </StyledLabelFieldDiv>

                <StyledLabelFieldDiv>
                    <Typography>{t('sso.settings-claim-ext-id')}</Typography>
                    <StyledTextField
                        name="settings__claim_ext_id"
                        value={formState.settings.claim_ext_id}
                        onChange={handleChange}
                        helperText={t('sso.settings-claim-ext-id-helper')}
                        size="small"
                    />
                </StyledLabelFieldDiv>

                <StyledLabelFieldDiv>
                    <Typography>{t('sso.auto-provision-users')}</Typography>
                    <Switch
                        name="settings__auto_provision_users"
                        checked={formState.settings.auto_provision_users}
                        onChange={handleChange}
                    />
                </StyledLabelFieldDiv>

                <StyledLabelFieldDiv>
                    <Typography>{t('sso.auto-update-users')}</Typography>
                    <Switch
                        name="settings__auto_update_users"
                        checked={formState.settings.auto_update_users}
                        onChange={handleChange}
                    />
                </StyledLabelFieldDiv>

                <StyledSaveButtonDiv>
                    {view === SsoView.Edit && (
                        <Button variant="contained" color="error" onClick={() => setDeleteDialogOpen(true)}>
                            {t('delete')}
                        </Button>
                    )}

                    <StyledLoadingButton
                        sx={{ marginLeft: '10px' }}
                        onClick={handleSave}
                        variant="contained"
                        disabled={checkForEmptyValues()}
                        loading={saving}
                    >
                        {t('save')}
                    </StyledLoadingButton>
                </StyledSaveButtonDiv>

                <Dialog open={deleteDialogOpen} onClose={handleDialogClose}>
                    <DialogTitle>{t('sso.delete-dialog-title')}</DialogTitle>

                    <DialogContent>
                        <DialogContentText color="text.primary">
                            {t('sso.delete-dialog-confirm', { displayName: formState?.display_name })}
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <Button variant="outlined" onClick={handleDialogClose}>
                            {t('cancel')}
                        </Button>
                        <StyledLoadingButton
                            variant="contained"
                            color="error"
                            onClick={handleDelete}
                            loading={deleting}
                        >
                            {t('delete')}
                        </StyledLoadingButton>
                    </DialogActions>
                </Dialog>
            </StyledFormDiv>
        </>
    );
};

export default SsoUpsert;
